import { jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { $definitions, } from 'ziphy-web-shared/basic/entities/notes/definitions';
import { areStringArraysEqual } from 'ziphy-web-shared/basic/helpers';
import { Dropdown, DropdownContent, useDropdown, } from 'ziphy-web-shared/basic/lib/functional';
import { BadgeNew } from 'ziphy-web-shared/basic/lib/simple';
import PlaceholderHelpers from '@nedoShared/modules/notes/placeholder/Placeholder.helpers';
import styles from './PlaceholderSelect.module.scss';
const { getBadgeColor } = PlaceholderHelpers;
const getArrayValue = (value) => Array.isArray(value) ? value : value ? [value] : [];
const getSelectedValues = (values, checkMap) => {
    return values.filter((item) => Boolean(checkMap[item]));
};
const getOtherValue = (values, checkMap) => {
    return values.find((item) => !Boolean(checkMap[item]));
};
const PlaceholderSelect = ({ elementId, data, onChange, isBlocked, isLoading, isPlain, isInvalid, }) => {
    var _a;
    const select = $definitions.selects.items[data.selectId];
    const selectValuesMap = $definitions.selects.valuesMap(data === null || data === void 0 ? void 0 : data.selectId);
    const multiValue = useMemo(() => (data.multi ? getArrayValue(data.value) : []), []);
    const [selected, setSelected] = useState(getSelectedValues(multiValue, selectValuesMap));
    const [otherValue, setOtherValue] = useState(data.multi
        ? getOtherValue(multiValue, selectValuesMap) || ''
        : typeof data.value === 'string'
            ? !selectValuesMap[data.value]
                ? data.value
                : ''
            : '');
    const handleDone = (onCloseDropdown) => {
        const selectedWithOther = otherValue ? [...selected, otherValue] : selected;
        const selectedWithUniqueValues = [...new Set(selectedWithOther)];
        const prevSelected = getArrayValue(data.value);
        if (areStringArraysEqual(selectedWithUniqueValues, prevSelected)) {
            onCloseDropdown();
        }
        else {
            onChange(data.id, { value: selectedWithUniqueValues });
        }
    };
    const handleChange = (value) => {
        if (data.multi) {
            setSelected((prevState) => {
                if (prevState.includes(value)) {
                    return prevState.filter((item) => item !== value);
                }
                else {
                    return [...prevState, value];
                }
            });
        }
        else {
            onChange(data.id, { value: value });
        }
    };
    const selectValues = useMemo(() => {
        if (!select)
            return [];
        return select.values.map((item) => ({
            id: item.code,
            label: item.title,
            description: item.description,
            selected: data.multi
                ? selected.some((value) => value === item.code)
                : item.code === data.value,
            onClick: () => handleChange(item.code),
        }));
    }, [select, selected.length, (_a = data.value) === null || _a === void 0 ? void 0 : _a.length]);
    const { dropdownRef, filteredItems, totalFilteredItems } = useDropdown(selectValues);
    const otherField = useMemo(() => {
        var _a, _b;
        if (!select.freeText)
            return;
        if (select.freeText.type === 'date') {
            return {
                element: 'date',
                field: {
                    value: otherValue,
                    onChange: data.multi ? setOtherValue : handleChange,
                },
                placeholder: (_a = select.freeText) === null || _a === void 0 ? void 0 : _a.placeholder,
            };
        }
        else if (select.freeText.type === 'text') {
            return {
                element: 'input',
                field: {
                    value: otherValue,
                    onChange: data.multi ? setOtherValue : handleChange,
                },
                placeholder: (_b = select.freeText) === null || _b === void 0 ? void 0 : _b.placeholder,
                submitMode: data.multi ? 'update-on-change' : 'update-on-submit',
            };
        }
    }, [otherValue]);
    return (_jsx(Dropdown, Object.assign({ ref: dropdownRef, id: elementId, className: styles.dropdown, "data-placeholder": true, onClickOutside: (setIsOpen) => {
            if (!isBlocked) {
                setIsOpen(false);
            }
        }, renderButton: ({ fieldRef, onToggle }) => {
            var _a;
            return (_jsx(BadgeNew, { valueId: `ref_${data.id}`, ref: fieldRef, placeholder: data.title, editable: !isBlocked && !data.blocked && !isPlain, color: getBadgeColor(data), value: Array.isArray(data.value)
                    ? data.value.map((value) => { var _a; return ((_a = selectValuesMap[value]) === null || _a === void 0 ? void 0 : _a.title) || value; }).join(', ')
                    : ((_a = selectValuesMap[data.value]) === null || _a === void 0 ? void 0 : _a.title) || data.value, onClick: () => onToggle(), className: classNames(styles.badge, {
                    [styles.badgeGray]: isBlocked,
                    [styles.badgePurple]: data.blocked && data.source === 'system',
                }), showTextSelection: false, isLoading: isLoading, isPlain: isPlain, isInvalid: isInvalid }));
        }, direction: ['bottom'], resizeDeps: [totalFilteredItems] }, { children: _jsx(DropdownContent, { items: filteredItems, isManualCloseControl: true, isMulti: data.multi, onDone: data.multi ? handleDone : undefined, title: select.freeText ? select.freeText.title : undefined, otherField: otherField }) })));
};
export default observer(PlaceholderSelect);
